import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Image, Text, View } from 'react-native';
import Helpers from '../../../../../Common/Helpers/AmountHelpers';
import Project from "../../../../../Common/DTO/Project";
import { styles } from './InvestBoxViewStyle';
import { translate } from "../../../../../Common/Utils/translation.utils";
import ProgressBarView from "./components/progressbar/ProgressBarView";
import { Application, LanguageManager, Managers, NavigationManager } from "@levelapp/softfabric";
import { getToken } from '../../../../../Common/Utils/token.utils';
import OpeningCountDownView from "./components/openingcountdown/OpeningCountDownView";
import { useWindowSizeContext } from "../../../../../Common/contexts/WindowSizeContext";
import { ButtonView } from "@levelapp/softfabric-ui";

interface InvestBoxViewProps {
    project: Project | undefined;
    investBoxSizeFactor: number;
    isProjectOpened: boolean;
    setIsProjectOpened: (newValue: boolean) => void;
}

export const InvestBoxView = ({
    project,
    investBoxSizeFactor,
    isProjectOpened,
    setIsProjectOpened
}: InvestBoxViewProps) => {

    const { width, height, isMobile } = useWindowSizeContext();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const wideScreenWidth = 600;
    const maxWidth = 450;

    const calculateBoxWidth = useCallback(() => {
        if (isMobile) {
            return width * 0.9 < wideScreenWidth ? width * 0.9 : maxWidth;
        }
        if (width * 0.225 >= maxWidth) return maxWidth;
        if (width >= 1750) return maxWidth;
        if (width >= 1250) return width * 0.25;
        return width * 0.275
    }, [isMobile, width, wideScreenWidth]);

    const getInfoViewClass = useCallback(() => {
        return isMobile ? styles.infoViewRow : styles.infoViewRow;
    }, [isMobile]);

    const calculateBoxFontSize = useCallback(() => {
        return boxWidth.current * 0.1 < 40 ? boxWidth.current * 0.1 : 40;
    }, [isMobile]);

    const getInfoTextStyles = useCallback(() => {
        return isMobile
            ? [styles.infoText, styles.textWhite, { fontSize: boxFontSize.current * 0.4, }]
            : [styles.infoText, styles.textWhite, { fontSize: boxFontSize.current * 0.4 }];
    }, [isMobile]);

    const getInfoValueStyles = useCallback(() => {
        return isMobile
            ? [styles.infoValue, styles.textWhite, { fontSize: boxFontSize.current * 0.4 }]
            : [styles.infoValue, styles.textWhite, { fontSize: boxFontSize.current * 0.4 }];
    }, []);

    const boxWidth: MutableRefObject<number> = useRef(calculateBoxWidth());
    const boxFontSize: MutableRefObject<number> = useRef(calculateBoxFontSize());
    const infoViewClass: MutableRefObject<any> = useRef(getInfoViewClass());
    const infoTextStyles: MutableRefObject<any> = useRef(getInfoTextStyles());
    const infoValueStyles: MutableRefObject<any> = useRef(getInfoValueStyles());

    const languageManager: LanguageManager = Application.current.resolve<LanguageManager>(Managers.LANGUAGE_MANAGER);

    useEffect(() => {
        boxWidth.current = calculateBoxWidth();
        boxFontSize.current = calculateBoxFontSize();
        infoViewClass.current = getInfoViewClass();
        infoTextStyles.current = getInfoTextStyles();
        infoValueStyles.current = getInfoValueStyles();
    }, [
        width,
        height,
        calculateBoxWidth,
        getInfoViewClass,
        calculateBoxFontSize,
        getInfoTextStyles,
        getInfoValueStyles,
    ]);



    const formatMaturity = () => {
        if (project === undefined) return "";
        const projectDuration = project.duration;
        const yearWord = projectDuration > 1 ? translate("years") : translate("year")
        return `${projectDuration} ${yearWord}`;
    }

    const formatRate = () => {
        return project !== undefined ? project.annualInterestRate + "%" : "0%";
    }

    const getToFinancedAmount = () => {
        if (!project) return "0€";
        const remaining = project.maximumInvest - project.currentInvest;

        return Helpers.formatAmount(remaining);
    }

    const onInvestButtonPress = () => {
        setIsLoading(true);
        const code = project?.code;
        const token = getToken();



        setIsLoading(false);



        if (token)
            Application.current?.resolve<NavigationManager>(Managers.NAVIGATION_MANAGER)?.navigate(`/invest/${code}`);
        else
            window.location.href = `https://my.beebonds.com/${languageManager.language}/login?redirect=https://invest.beebonds.com/${languageManager.language}/invest/${code}`;
    }

    const onRegisterPressButton = () => {
        const languageManager = Application.current.resolve<LanguageManager>(Managers.LANGUAGE_MANAGER);
        window.location.href = `https://my.beebonds.com/${languageManager.language}/register`;
    }

    const navigateToDocument = () => {
        document.getElementById('documents')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (!project) return null;

    return (
        <View style={[styles.box, {
            width: isMobile ? boxWidth.current : '100%',
            paddingVertical: 52 * investBoxSizeFactor
        }]}>
            <Image source={require('./assets/background.png')} height={690 * investBoxSizeFactor}
                width={448 * investBoxSizeFactor} style={styles.imageBackground} />
            {!isProjectOpened ? (
                <OpeningCountDownView openingDate={new Date(project.openingDate)}
                    setIsProjectOpened={setIsProjectOpened} />
            ) : (
                <>
                    <Text style={[styles.title, styles.textWhite, {
                        fontSize: boxFontSize.current,
                        marginBottom: 38 * investBoxSizeFactor
                    }]}>{Helpers.formatAmount(project.currentInvest)}</Text>
                    <View style={[isMobile ? styles.amountsViewCol : styles.amountsBeforBarView]}>
                        <View style={styles.amountsViewsLeft}>
                            <Text
                                style={[styles.amountsText, styles.textWhite, { fontSize: boxFontSize.current * 0.4 }]}>Min
                                : {Helpers.formatAmount(project.minimumInvest)}</Text>
                        </View>
                        <View style={styles.amountsViewsRight}>
                            <Text
                                style={[styles.amountsTextRight, styles.textWhite, { fontSize: boxFontSize.current * 0.4 }]}>Max
                                : {Helpers.formatAmount(project.maximumInvest)}</Text>
                        </View>
                    </View>
                    <ProgressBarView project={project} isMobile={isMobile} />
                    <View style={[isMobile ? styles.amountsViewCol : styles.amountsViewRow]}>
                        <View style={styles.amountsViewsLeft}>
                            <Text
                                style={[styles.amountsText, styles.textWhite, { fontSize: boxFontSize.current * 0.4 }]}>{translate("financed")} : {Helpers.formatAmount(project.currentInvest)}</Text>
                        </View>
                        <View style={styles.amountsViewsRight}>
                            <Text
                                style={[styles.amountsTextRight, styles.textWhite, { fontSize: boxFontSize.current * 0.4 }]}>{translate("toFinance")} : {getToFinancedAmount()}</Text>
                        </View>
                    </View>
                </>
            )}
            <View>
                <Text
                    style={[styles.titleText, styles.textWhite, {
                        fontSize: boxFontSize.current * 1,
                        marginTop: 32 * investBoxSizeFactor,
                        marginBottom: 32 * investBoxSizeFactor
                    }]}
                >
                    {project.projectName.toUpperCase()}
                </Text>
            </View>
            <View style={{ width: '95%' }}>
                {project.issueDate ? (
                    <View style={infoViewClass.current}>
                        <Text style={infoTextStyles.current}>{translate("issueDate")}</Text>
                        <Text style={infoValueStyles.current}>{project.issueDate}</Text>
                    </View>
                ) : null}

                {project.offerEndDate ? (
                    <View style={infoViewClass.current}>
                        <Text style={infoTextStyles.current}>{translate("endDate")}</Text>
                        <Text style={infoValueStyles.current}>{project.offerEndDate}</Text>
                    </View>
                ) : null}

                {project.duration ? (
                    <View style={infoViewClass.current}>
                        <Text style={infoTextStyles.current}>{translate("maturity")}</Text>
                        <Text style={infoValueStyles.current}>{formatMaturity()}</Text>
                    </View>
                ) : null}
                {project.annualInterestRate ? (
                    <View style={infoViewClass.current}>
                        <Text style={infoTextStyles.current}>{translate("rate")}</Text>
                        <Text style={infoValueStyles.current}>{formatRate()}</Text>
                    </View>
                ) : null}
                <View style={infoViewClass.current}>
                    <Text style={infoTextStyles.current}>{translate("minimiumInvest")}</Text>
                    <Text style={infoValueStyles.current}>{Helpers.formatAmount(project.minimumTicket)}</Text>
                </View>
            </View>
            {isProjectOpened && project.projectTypeId != 3 && (
                <View style={[styles.btnContainer, { marginTop: 36 * investBoxSizeFactor }]}>
                    <ButtonView handleClick={onInvestButtonPress} isLoading={isLoading} animation="None"
                        borders="Little" backgroundColor="#FFA93B">
                        <View style={{
                            ...styles.btn,
                            width: boxWidth.current * 0.6
                        }}>
                            <Text
                                style={[styles.btnText, styles.textGrey, { fontSize: boxFontSize.current * 0.5 }]}>{project.code === 'rwdm' ? translate("participate") : translate("invest")}</Text>
                        </View>
                    </ButtonView>
                </View>
            )}
            <View style={{ marginTop: 20 }}>
                <Text style={[infoTextStyles.current, { textAlign: 'center' }]}>
                    {translate("findDocument")}
                    <Text
                        style={{ color: '#FFA93B', textDecorationLine: 'underline', }}
                        onPress={() => navigateToDocument()}
                    >
                        {translate("here")}
                    </Text>
                </Text>
            </View>
            <View style={{ marginTop: 20 }}>
                <Text style={infoTextStyles.current}>
                    {translate("noAccount")}
                    <Text
                        style={{ color: '#FFA93B', textDecorationLine: 'underline', }}
                        onPress={() => onRegisterPressButton()}
                    >
                        {translate("noAccount.cta")}
                    </Text>
                </Text>
            </View>
        </View>
    );
}

export default InvestBoxView;